/**
 * FollowPill — LAW 12's whole user interface.
 *
 * design.md §5.5, literally: *"A manual turn while playing sets `following = false` and raises a
 * non-modal pill: **'Karti is on page 14 · Back to the narration'**, which turns but does not seek.
 * Auto-refollow when the user lands back on the narration's page, or after 20 s idle with that page
 * displayed. Never by moving the user."*
 *
 * ── Why a pill and not a snap-back ──────────────────────────────────────────
 * **LAW 12. Turning a page is browsing. It never moves the narration.** §5.5 calls this "the most
 * important interaction decision in the reader", and the reason is one sentence: conflating them
 * means you cannot flip back to check a name without the voice jumping, which is the single most
 * common thing a listener does. So the reader flips freely, the voice keeps reading, and this is the
 * only thing on screen that says where the voice went.
 *
 * Three properties this component has to keep, each of which is a way the interaction dies:
 *
 * 1. **It never moves anybody.** It is a `<button>`; nothing here fires on a timer. The auto-refollow
 *    §5.5 describes sets a *flag* in `usePageTurn` and does not call `goTo` — see the effect there.
 * 2. **It states the page it would take you to, before you tap it.** "Back to the narration" alone
 *    is a control whose effect you learn by using it; "Karti is on page 14" is the same control with
 *    the answer already in it, and half the time reading the number is all anyone wanted.
 * 3. **It never says "seek", "resume" or "play".** Those are three different things this button does
 *    not do, and a listener who taps expecting the audio to jump to the page they were reading has
 *    been told the wrong thing by the label.
 *
 * ⚠️ The page number is **display state, never storage** (§5.1: "Page numbers are never persisted").
 * Nothing may read it back into a position — a position is a sid.
 */

import type { ReactElement } from "react";

export interface FollowPillProps {
  /**
   * 1-based **turn** number, not column index. In spread mode one page-turn shows two columns, and
   * a pill that says "page 14" while the footer says "page 7 of 21" is two widgets disagreeing
   * about the same book. `Paginator.turnIndex()` is the one arithmetic.
   */
  page: number;
  /**
   * Who is reading. Almost always "Karti" — this book's whole premise is a cloned voice with a name
   * — but the voice is a row in `voices`, not a constant, and a book narrated by `default` saying
   * "Karti is on page 14" is a small lie told on every page turn.
   */
  voice: string;
  /** Turns. Does **not** seek. */
  onBack(): void;
  /**
   * P5's other tenant (§11: "FollowPill.tsx — §5.5 / doubles as co-read presence"). A room member
   * reading elsewhere raises the same pill with their name and their page. Shaped here so that phase
   * adds a caller rather than a second pill; **not built** — nothing passes it and nothing renders
   * a difference yet.
   */
  presence?: null;
}

export function FollowPill(props: FollowPillProps): ReactElement {
  const { page, voice, onBack } = props;

  return (
    <button
      type="button"
      className="ab-pill"
      onClick={onBack}
      // Polite, never assertive. This appears the instant a finger turns a page; interrupting the
      // sentence a screen-reader user is in the middle of, to tell them where the audio is, is the
      // opposite of non-modal.
      aria-live="polite"
      // The accessible name has to carry what the visual separator carries. A screen reader reading
      // "Karti is on page 14 middle dot Back to the narration" is not a sentence.
      aria-label={`${voice} is on page ${page}. Go back to the narration.`}
    >
      <span>
        {voice} is on page {page}
      </span>
      <span aria-hidden="true" style={{ opacity: 0.5 }}>
        ·
      </span>
      <span>Back to the narration</span>
    </button>
  );
}

export default FollowPill;
